import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import type { ApiError } from '@pbt/pbt-ui-components'

import type { ReminderProtocol } from '~/api/graphql/generated/types'
import { getErrorMessage } from '~/utils/errors'

import type { RootState } from '../index'

export type ReminderProtocolsState = {
  error: string | null
  isLoading: boolean
  // key is either procedureId or priceId:procedureId
  map: Record<string, ReminderProtocol[]>
}

export const INITIAL_STATE: ReminderProtocolsState = {
  map: {},
  isLoading: false,
  error: null,
}

export function getKey(
  priceId: string | null,
  procedureId: string,
  patientSpeciesId?: string,
) {
  const baseKey = priceId ? `${priceId}:${procedureId}` : procedureId
  return patientSpeciesId ? `${baseKey}:${patientSpeciesId}` : baseKey
}

const reminderProtocolsSlice = createSlice({
  name: 'reminderProtocolsV2',
  initialState: INITIAL_STATE,
  reducers: {
    fetchReminderProtocolsByProcedureOnly: (
      state,
      // eslint-disable-next-line unused-imports/no-unused-vars
      action: PayloadAction<{ patientSpeciesId?: string; procedureId: string }>,
    ) => {
      state.isLoading = true
      state.error = null
    },
    fetchReminderProtocolsByProcedureOnlySuccess: (
      state,
      {
        payload,
      }: PayloadAction<{
        patientSpeciesId?: string
        procedureId: string
        protocols: ReminderProtocol[]
      }>,
    ) => {
      state.isLoading = false
      state.map[getKey(null, payload.procedureId, payload.patientSpeciesId)] =
        payload.protocols
    },
    fetchReminderProtocolsByProcedureOnlyFailure: (
      state,
      { payload }: PayloadAction<{ error: ApiError }>,
    ) => {
      state.isLoading = false
      state.error = getErrorMessage(payload.error)
    },
    fetchReminderProtocolsByPriceAndProcedure: (
      state,
      // eslint-disable-next-line unused-imports/no-unused-vars
      action: PayloadAction<{ priceId: string; procedureId: string }>,
    ) => {
      state.isLoading = true
      state.error = null
    },
    fetchReminderProtocolsByPriceAndProcedureSuccess: (
      state,
      {
        payload,
      }: PayloadAction<{
        priceId: string
        procedureId: string
        protocols: ReminderProtocol[]
      }>,
    ) => {
      state.isLoading = false
      state.map[getKey(payload.priceId, payload.procedureId)] =
        payload.protocols
    },
    fetchReminderProtocolsByPriceAndProcedureFailure: (
      state,
      { payload }: PayloadAction<{ error: ApiError }>,
    ) => {
      state.isLoading = false
      state.error = getErrorMessage(payload.error)
    },
    // TODO: as part of CVC-15308 we should stop using these old actions
    fetchReminderProtocols: (
      state,
      // eslint-disable-next-line unused-imports/no-unused-vars
      action: PayloadAction<{ priceId: string; procedureId: string }>,
    ) => {
      state.isLoading = true
      state.error = null
    },
    fetchReminderProtocolsSuccess: (
      state,
      {
        payload,
      }: PayloadAction<{
        priceId: string
        procedureId: string
        protocols: ReminderProtocol[]
      }>,
    ) => {
      state.isLoading = false
      state.map[getKey(payload.priceId, payload.procedureId)] =
        payload.protocols
    },
    fetchReminderProtocolsFailure: (
      state,
      { payload }: PayloadAction<{ error: ApiError }>,
    ) => {
      state.isLoading = false
      state.error = getErrorMessage(payload.error)
    },
  },
})

const { actions, reducer } = reminderProtocolsSlice

export const {
  fetchReminderProtocolsByPriceAndProcedure,
  fetchReminderProtocolsByPriceAndProcedureSuccess,
  fetchReminderProtocolsByPriceAndProcedureFailure,
  fetchReminderProtocolsByProcedureOnly,
  fetchReminderProtocolsByProcedureOnlySuccess,
  fetchReminderProtocolsByProcedureOnlyFailure,
  fetchReminderProtocols,
  fetchReminderProtocolsSuccess,
  fetchReminderProtocolsFailure,
} = actions

export default reducer
export const getReminderProtocolsState = (
  state: RootState,
): ReminderProtocolsState => state.reminderProtocolsV2
export const getReminderProtocolsIsLoading = (state: RootState) =>
  getReminderProtocolsState(state).isLoading
export const getReminderProtocolsMap = (state: RootState) =>
  getReminderProtocolsState(state).map
export const getReminderProtocolsByPriceAndProcedure = (
  priceId: string,
  procedureId: string,
) =>
  createSelector(
    getReminderProtocolsMap,
    (map) => map[getKey(priceId, procedureId)] ?? [],
  )
export const getReminderProtocolsByProcedureOnly = (
  procedureId: string,
  patientSpeciesId?: string,
) =>
  createSelector(
    getReminderProtocolsMap,
    (map) => map[getKey(null, procedureId, patientSpeciesId)] ?? [],
  )
// TODO deprecated as part of CVC-15308
export const getReminderProtocols = (priceId: string, procedureId: string) =>
  createSelector(
    getReminderProtocolsMap,
    (map) => map[getKey(priceId, procedureId)] ?? [],
  )
